import { ptBR as corePtBR } from '@mui/material/locale'
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { ptBR as dataGridPtBR } from '@mui/x-data-grid/locales'
import { ptBR as pickersPtBR } from '@mui/x-date-pickers/locales'
import {
  createContext,
  createElement,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {
  CheckboxSmallChecked,
  CheckboxSmallEmptyOutlined,
  CheckboxSmallIndeterminate,
} from '@/ui/icons/form/mui-checkbox'
import { SCREENS } from '@/ui/screens'
import {
  type ModeVariant,
  THEME_DEFAULTS,
  THEME_MODE_OPTIONS,
  THEME_OPTIONS,
  THEME_STORAGE_KEYS,
  type ThemeVariant,
} from './config'
import MuiLayerOverride from './mui-layer-override'

type ThemeContextType = {
  theme: ThemeVariant
  setTheme: (theme: ThemeVariant) => void
  mode: ModeVariant
  setMode: (mode: ModeVariant) => void
  isDarkMode: boolean
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

const px = (value: string): number => Number.parseInt(value, 10)

// As cores são as variáveis HSL de ui/style/theme: trocar de cor ou de modo
// é trocar classe em <html>, sem recriar o tema.
export const createMuiTheme = () =>
  createTheme(
    {
      spacing: 4,
      breakpoints: {
        values: {
          xs: 0,
          sm: px(SCREENS.sm),
          md: px(SCREENS.md),
          lg: px(SCREENS.lg),
          xl: px(SCREENS.xl),
          '2xl': px(SCREENS['2xl']),
          '3xl': px(SCREENS['3xl']),
        },
      },
      cssVariables: { colorSchemeSelector: 'class' },
      palette: {
        background: {
          default: 'hsl(var(--background))',
          paper: 'hsl(var(--background-paper))',
        },
        divider: 'hsl(var(--grey-100))',
        text: {
          primary: 'hsl(var(--text-primary))',
          secondary: 'hsl(var(--text-secondary))',
          disabled: 'hsl(var(--text-disabled))',
        },
        primary: paletteColor('primary', 'primary-contrast'),
        secondary: paletteColor('secondary', 'secondary-contrast'),
        action: {
          disabled: 'hsla(var(--text-disabled) / .95)',
          disabledBackground: 'hsla(var(--text-disabled) / .5)',
          active: 'hsl(var(--text-primary))',
        },
        'accent-1': paletteColor('accent-1', 'accent-1-contrast'),
        'accent-2': paletteColor('accent-2', 'accent-2-contrast'),
        'accent-3': paletteColor('accent-3', 'accent-3-contrast'),
        'accent-4': paletteColor('accent-4', 'accent-4-contrast'),
        'text-primary': paletteColor('text-primary', 'text-contrast-alternative'),
        'text-secondary': paletteColor('text-secondary', 'text-contrast-alternative'),
        'text-disabled': paletteColor('text-disabled', 'text-contrast-alternative'),
        'text-muted': paletteColor('text-muted', 'text-contrast-alternative'),
        grey: {
          25: 'hsl(var(--grey-25))',
          50: 'hsl(var(--grey-50))',
          100: 'hsl(var(--grey-100))',
          200: 'hsl(var(--grey-200))',
          300: 'hsl(var(--grey-300))',
          400: 'hsl(var(--grey-400))',
          500: 'hsl(var(--grey-500))',
          600: 'hsl(var(--grey-600))',
          700: 'hsl(var(--grey-700))',
          800: 'hsl(var(--grey-800))',
          900: 'hsl(var(--grey-900))',
        },
        error: paletteColor('error', 'error-contrast'),
        info: paletteColor('info', 'info-contrast'),
        success: paletteColor('success', 'success-contrast'),
        warning: paletteColor('warning', 'warning-contrast'),
      },
      // Gaveta e diálogo entram desacelerando longo, como o resto de ui/style/motion.css.
      transitions: {
        easing: { easeOut: 'cubic-bezier(0.16, 1, 0.3, 1)' },
        duration: { enteringScreen: 320, leavingScreen: 200 },
      },
      components: {
        MuiCircularProgress: { defaultProps: { size: '1.5rem' } },
        MuiSkeleton: { defaultProps: { animation: 'wave' } },
        MuiCheckbox: {
          defaultProps: {
            icon: createElement(CheckboxSmallEmptyOutlined),
            checkedIcon: createElement(CheckboxSmallChecked),
            indeterminateIcon: createElement(CheckboxSmallIndeterminate),
          },
        },
        MuiSwitch: {
          defaultProps: { disableRipple: true, disableFocusRipple: true, disableTouchRipple: true },
        },
        MuiInput: { defaultProps: { disableUnderline: true } },
        MuiButtonBase: { defaultProps: { disableRipple: true, disableTouchRipple: true } },
        // Botão tem onda no clique, tingida em motion.css. O foco já tem anel
        // próprio, então a onda de foco fica desligada.
        MuiButton: {
          defaultProps: {
            disableElevation: true,
            disableRipple: false,
            disableTouchRipple: false,
            disableFocusRipple: true,
          },
        },
        MuiIconButton: {
          defaultProps: {
            disableRipple: false,
            disableTouchRipple: false,
            disableFocusRipple: true,
          },
        },
        MuiButtonGroup: { defaultProps: { disableElevation: true, disableRipple: true } },
        MuiAccordion: { defaultProps: { elevation: 0 } },
        MuiPaper: { defaultProps: { elevation: 0 } },
        MuiCard: { defaultProps: { elevation: 2 } },
      },
    },
    corePtBR,
    pickersPtBR,
    dataGridPtBR,
  )

// O texto sobre cada cor cheia vem de style/theme/contrast.css, medido por tema.
function paletteColor(name: string, contrast: string) {
  return {
    main: `hsl(var(--${name}))`,
    light: `hsl(var(--${name}-light))`,
    dark: `hsl(var(--${name}-dark))`,
    contrastText: `hsl(var(--${contrast}))`,
  }
}

// Modo privado e armazenamento bloqueado lançam; o tema cai no padrão.
function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const value = localStorage.getItem(key)
    return allowed.find((option) => option === value) ?? fallback
  } catch {
    return fallback
  }
}

function writeStored(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    // Sem armazenamento a escolha vale só até recarregar.
  }
}

const themeValues = Object.values(THEME_OPTIONS)

export default function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeVariant>(() =>
    readStored(THEME_STORAGE_KEYS.themeColor, themeValues, THEME_DEFAULTS.themeColor),
  )
  const [mode, setModeState] = useState<ModeVariant>(() =>
    readStored(THEME_STORAGE_KEYS.themeMode, THEME_MODE_OPTIONS, THEME_DEFAULTS.themeMode),
  )
  const [isDarkMode, setIsDarkMode] = useState(false)
  const muiTheme = useMemo(() => createMuiTheme(), [])

  const setTheme = (next: ThemeVariant) => {
    setThemeState(next)
    writeStored(THEME_STORAGE_KEYS.themeColor, next)
  }
  const setMode = (next: ModeVariant) => {
    setModeState(next)
    writeStored(THEME_STORAGE_KEYS.themeMode, next)
  }

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const apply = () => {
      const finalMode = mode === 'system' ? (media.matches ? 'dark' : 'light') : mode
      const classList = document.documentElement.classList
      classList.remove(...themeValues, 'light', 'dark')
      classList.add(theme, finalMode)
      setIsDarkMode(finalMode === 'dark')
    }
    apply()
    if (mode !== 'system') return
    media.addEventListener('change', apply)
    return () => media.removeEventListener('change', apply)
  }, [theme, mode])

  // Sem transição na primeira pintura, senão a página "desliza" do padrão
  // para a cor salva.
  useEffect(() => {
    const id = setTimeout(
      () =>
        document.documentElement.style.setProperty(
          '--layout-duration',
          `${THEME_DEFAULTS.transitionDuration}ms`,
        ),
      200,
    )
    return () => clearTimeout(id)
  }, [])

  return (
    <ThemeContext.Provider value={{ theme, setTheme, mode, setMode, isDarkMode }}>
      <MuiLayerOverride />
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}

export const useThemeContext = () => {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useThemeContext fora do ThemeProvider')
  return context
}
